import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Link, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import gaiaTheme from '../../../../root-components/theme';
import { BackButtonLink } from '../../../presentation-components/display-elements-components/back-button-link';
import { CloseOutlined, DeleteOutline, EditOutlined, LockOpenOutlined, StarOutline } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import { IArchivedAndReportPeriodDetails } from '../../../../shared/services/report-period/report-period.model';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { NumericFormat } from 'react-number-format';
import { IPostArchivedReportPeriod } from '../../../../shared/services/archived-report-period/archived-report-period.model';
import ArchivedReportPeriodService from '../../../../shared/services/archived-report-period/archived-report-period.service';
import TargetSettingService from '../../../../shared/services/target-setting/target-setting.service';
import { AddArchivedReportPeriodDialog } from '../../../dialogs/settings/report-period/add-archived-report-period-dialog';
import { ArchivedBreakdownTable } from '../../../presentation-components/settings-components/archived-breakdown-fields';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { PageTitle } from '../../../presentation-components/display-elements-components/page-title';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { CloseReportPeriodDialog } from '../../../dialogs/settings/report-period/close-report-period-dialog';
import { FormTextInputDisplay } from '../../../presentation-components/form-field-components/new-and-edit/form-text-input';
import { FormDateInputDisplay } from '../../../presentation-components/form-field-components/new-and-edit/form-date-input';
import { LoadingPage } from '../../loading-page';
import { LoadingButton } from '@mui/lab';
import { ReportPeriodConversionFactorsTable } from '../../../presentation-components/settings-components/report-period-conversion-factors-table';
import { ChangeConversionYearDialog } from '../../../dialogs/settings/report-period/change-conversion-year-dialog';
import { ReportPeriodExchangeRatesSection } from '../../../presentation-components/settings-components/report-period-exchange-rates-section';

export const SettingsReportingPeriodDetailPage = () => {

  const navigate = useNavigate();
  const { id } = useParams();
  //Services
  const archivedReportPeriodService = new ArchivedReportPeriodService();
  const reportPeriodService = new ReportPeriodService();
  const targetSettingService = new TargetSettingService()
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  //State
  const [reportPeriodDetails, setReportPeriodDetails] = useState<IArchivedAndReportPeriodDetails>()
  const [openDialog, setDialogOpen] = React.useState(false);
  const [closeReportDialogOpen, setCloseReportDialogOpen] = useState<boolean>(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [hasReductionPlan, setHasReductionPlan] = useState<boolean>(false)
  const [searchParams] = useSearchParams();
  // ?tab=conversion-factors deep-links straight to the Conversion Factors tab
  // (used by the factor-year review banner and notification email).
  const [value, setValue] = React.useState(searchParams.get('tab') === 'conversion-factors' ? 2 : 0)
  const [totalRevenueInput, setTotalRevenueInput] = useState<string>('')
  const [totalRevenueError, setTotalRevenueError] = useState<string>('')
  const [balanceSheetTotalInput, setBalanceSheetTotalInput] = useState<string>('')
  const [balanceSheetTotalError, setBalanceSheetTotalError] = useState<string>('')
  const [reportingDeadlineInput, setReportingDeadlineInput] = useState<string>('')
  const [reportingDeadlineError, setReportingDeadlineError] = useState<string>('')
  const [reminderDaysBeforeInput, setReminderDaysBeforeInput] = useState<string>('')
  const [reminderDaysBeforeError, setReminderDaysBeforeError] = useState<string>('')
  const [numberOfUkEmployeesInput, setNumberOfUkEmployeesInput] = useState<string>('')
  const [numberOfNonUkEmployeesInput, setNumberOfNonUkEmployeesInput] = useState<string>('')
  const [numberOfUkEmployeesError, setNumberOfUkEmployeesError] = useState<string>('')
  const [numberOfNonUkEmployeesError, setNumberOfNonUkEmployeesError] = useState<string>('')
  const [editField, setEditField] = useState<string>('')
  const [changeYearDialogOpen, setChangeYearDialogOpen] = useState<boolean>(false)
  const [selectedConversionSourceId, setSelectedConversionSourceId] = useState<string>('')
  const [selectedConversionSourceName, setSelectedConversionSourceName] = useState<string>('')
  const [selectedAvailableYears, setSelectedAvailableYears] = useState<string[]>([])
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Variables
  const companyName = (!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? userDetails?.partnerOrganisation?.partnerName : 'Gaia';
  const areActionsDisabled = isDeleting;

  useEffect(() => {
    getReportPeriodDetails();
    targetSettingService.getTargetSettings()
      .then(response => {
        if (response.length > 0) {
          setHasReductionPlan(true)
        }
      })
      .catch(err => console.log(err))
  }, [])

  useEffect(() => {
    if (reportPeriodDetails && !reportPeriodDetails.manualEntry && !reportPeriodDetails.locked) {
      if (reportPeriodDetails.totalRevenue || reportPeriodDetails.totalRevenue === 0) {
        setTotalRevenueInput(String(reportPeriodDetails.totalRevenue))
      } else {
        setTotalRevenueInput('')
      }
      setNumberOfUkEmployeesInput(reportPeriodDetails.numberOfUkEmployees != null ? String(reportPeriodDetails.numberOfUkEmployees) : '')
      setNumberOfNonUkEmployeesInput(reportPeriodDetails.numberOfNonUkEmployees != null ? String(reportPeriodDetails.numberOfNonUkEmployees) : '')
      setReportingDeadlineInput(reportPeriodDetails.reportingDeadline ? String(reportPeriodDetails.reportingDeadline).slice(0, 10) : '')
      setReminderDaysBeforeInput(reportPeriodDetails.reminderDaysBefore != null ? String(reportPeriodDetails.reminderDaysBefore) : '')
    }
    // Balance sheet stays editable on locked periods (statutory accounts backfill), so its
    // input seeds for any non-archived period.
    if (reportPeriodDetails && !reportPeriodDetails.manualEntry) {
      setBalanceSheetTotalInput(reportPeriodDetails.balanceSheetTotal != null ? String(reportPeriodDetails.balanceSheetTotal) : '')
    }
  }, [reportPeriodDetails])

  const getReportPeriodDetails = () => {
    if (id) {
      setIsLoading(true)
      reportPeriodService.getReportPeriodorArchivedReportPeriod(id)
        .then(response => {
          setReportPeriodDetails(response)
          setIsLoading(false)
        }
        ).catch(err => {
          console.log(err)
          setIsLoading(false)
        })
    }
  }

  const openDialogHandler = () => {
    setDialogOpen(true);
  }

  const closeDialogHandler = () => {
    setDialogOpen(false);
  };

  const editBaseYearHandler = () => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Are you sure you would like to record this report period as your base year?`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response && reportPeriodDetails) {
          reportPeriodService.updateBaseYear(reportPeriodDetails.id, reportPeriodDetails.manualEntry)
            .then(response => {
              getReportPeriodDetails();
              reportPeriodContext.forceUpdateReportPeriods();
            })
        }
      })
  }

  const deleteHandler = (baseYear: boolean, hasReductionPlan: boolean, manualEntry: boolean) => {
    if (isDeleting) {
      return;
    }
    let message
    if (manualEntry) {
      message = `Are you sure you would like to delete this report period? This action cannot be undone.`;
      if (baseYear && hasReductionPlan) {
        message = `Deleting this report period will delete your reduction targets, since they were calculated from this year’s figures. Are you sure you would like to delete this report period?`;
      }
    } else {
      message = `Are you sure you would like to delete this report period, this will delete all contents of this report period? This action cannot be undone.`;
      if (baseYear && hasReductionPlan) {
        message = `Deleting this report period will delete all contents of this report period, and your reduction targets, since they were calculated from this year’s figures. Are you sure you would like to delete this report period?`;
      }
    }
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: message,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response && id && manualEntry) {
          setIsDeleting(true)
          archivedReportPeriodService.deleteArchivedReportPeriod(id)
            .then(response => {
              navigate('/settings/company-settings/reporting-periods')
              userNotificationsContext.openSnackbar({ open: true, message: `Deleted successfully` });
              reportPeriodContext.forceUpdateReportPeriods();
            })
            .catch(err => {
              console.log(err)
              userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
            })
            .finally(() => {
              setIsDeleting(false)
            })
        } else if (response && id && !manualEntry) {
          setIsDeleting(true)
          reportPeriodService.deleteReportPeriod(id)
            .then(response => {
              navigate('/settings/company-settings/reporting-periods')
              userNotificationsContext.openSnackbar({ open: true, message: `Deleted successfully` });
              reportPeriodContext.forceUpdateReportPeriods();
            })
            .catch(err => {
              console.log(err)
              if (!err.response || err?.response?.status === 504) {
                navigate('/time-out?type=bulkDelete')
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
              }
            })
            .finally(() => {
              setIsDeleting(false)
            })
        }
      })
  }

  const cancelEditHandler = () => {
    setEditField('');
    setTotalRevenueError('');
    setNumberOfUkEmployeesError('');
    setNumberOfNonUkEmployeesError('');
    setBalanceSheetTotalError('');
    setReportingDeadlineError('');
    setReminderDaysBeforeError('');
    if (reportPeriodDetails) {
      if (reportPeriodDetails.totalRevenue || reportPeriodDetails.totalRevenue === 0) {
        setTotalRevenueInput(String(reportPeriodDetails.totalRevenue))
      } else {
        setTotalRevenueInput('')
      }
      setNumberOfUkEmployeesInput(reportPeriodDetails.numberOfUkEmployees != null ? String(reportPeriodDetails.numberOfUkEmployees) : '')
      setNumberOfNonUkEmployeesInput(reportPeriodDetails.numberOfNonUkEmployees != null ? String(reportPeriodDetails.numberOfNonUkEmployees) : '')
      setBalanceSheetTotalInput(reportPeriodDetails.balanceSheetTotal != null ? String(reportPeriodDetails.balanceSheetTotal) : '')
      setReportingDeadlineInput(reportPeriodDetails.reportingDeadline ? String(reportPeriodDetails.reportingDeadline).slice(0, 10) : '')
      setReminderDaysBeforeInput(reportPeriodDetails.reminderDaysBefore != null ? String(reportPeriodDetails.reminderDaysBefore) : '')
    }
  }

  // The whole details panel is one form; the active inline field is tracked by editField,
  // so dispatch the submit to the matching save handler.
  const saveDetailsHandler = (e: React.FormEvent) => {
    if (editField === 'numberOfUkEmployees') {
      saveEmployeeCountHandler('numberOfUkEmployees', numberOfUkEmployeesInput, setNumberOfUkEmployeesError)(e);
      return;
    }
    if (editField === 'numberOfNonUkEmployees') {
      saveEmployeeCountHandler('numberOfNonUkEmployees', numberOfNonUkEmployeesInput, setNumberOfNonUkEmployeesError)(e);
      return;
    }
    if (editField === 'balanceSheetTotal') {
      saveBalanceSheetTotalHandler(e);
      return;
    }
    if (editField === 'reportingDeadline' || editField === 'reminderDaysBefore') {
      saveReportingDeadlineHandler(e);
      return;
    }
    saveTotalRevenueHandler(e);
  }

  // Balance-sheet total feeds the SECR/ESOS applicability tests; blank clears it (the
  // regulation card then reports the criterion as unknown rather than assuming zero).
  const saveBalanceSheetTotalHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setBalanceSheetTotalError('');
    if (!reportPeriodDetails) {
      return;
    }
    let parsed: number | null = null;
    if (balanceSheetTotalInput !== '') {
      parsed = Number(balanceSheetTotalInput);
      if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
        setBalanceSheetTotalError('Your balance sheet total must be a whole number');
        return;
      }
    }
    reportPeriodService.updateDenominators(reportPeriodDetails.id, { balanceSheetTotal: parsed })
      .then(() => {
        setReportPeriodDetails({ ...reportPeriodDetails, balanceSheetTotal: parsed });
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
      })
      .catch(err => {
        console.log(err);
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
      });
  }

  const saveReportingDeadlineHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setReportingDeadlineError('');
    setReminderDaysBeforeError('');
    if (!reportPeriodDetails) {
      return;
    }
    if (reminderDaysBeforeInput !== '') {
      const days = Number(reminderDaysBeforeInput);
      if (!Number.isInteger(days) || days < 1 || days > 365) {
        setReminderDaysBeforeError('Enter a whole number of days between 1 and 365');
        return;
      }
    }
    reportPeriodService.updateReportingDeadline(reportPeriodDetails.id, {
      reportingDeadline: reportingDeadlineInput || null,
      reminderDaysBefore: reminderDaysBeforeInput === '' ? null : Number(reminderDaysBeforeInput),
    })
      .then(() => {
        setReportPeriodDetails({
          ...reportPeriodDetails,
          reportingDeadline: reportingDeadlineInput || null,
          reminderDaysBefore: reminderDaysBeforeInput === '' ? null : Number(reminderDaysBeforeInput),
        });
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
      })
      .catch(err => {
        console.log(err);
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
      });
  }

  const saveTotalRevenueHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setTotalRevenueError('');
    if (!reportPeriodDetails) {
      return;
    }
    if (totalRevenueInput === '') {
      setTotalRevenueError('Total revenue is required');
      return;
    }
    const parsed = Number(totalRevenueInput);
    if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
      setTotalRevenueError('Your total revenue must be a whole number');
      return;
    }
    reportPeriodService.updateTotalRevenue(reportPeriodDetails.id, parsed)
      .then(() => {
        setReportPeriodDetails({ ...reportPeriodDetails, totalRevenue: parsed });
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
      })
      .catch(err => {
        console.log(err);
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
      });
  }

  const applyLocationFigures = () => {
    if (!reportPeriodDetails) return;
    const uk = reportPeriodDetails.locationDerivedUkEmployees ?? 0;
    const nonUk = reportPeriodDetails.locationDerivedNonUkEmployees ?? 0;
    reportPeriodService.updateDenominators(reportPeriodDetails.id, { numberOfUkEmployees: uk, numberOfNonUkEmployees: nonUk })
      .then(() => {
        setReportPeriodDetails({ ...reportPeriodDetails, numberOfUkEmployees: uk, numberOfNonUkEmployees: nonUk });
        setNumberOfUkEmployeesInput(String(uk));
        setNumberOfNonUkEmployeesInput(String(nonUk));
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
      })
      .catch(err => {
        console.log(err);
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
      });
  }

  const saveEmployeeCountHandler = (
    field: 'numberOfUkEmployees' | 'numberOfNonUkEmployees',
    inputValue: string,
    setError: (msg: string) => void,
  ) => (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!reportPeriodDetails) {
      return;
    }
    if (inputValue === '') {
      setError('A number of employees is required');
      return;
    }
    const parsed = Number(inputValue);
    if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
      setError('The number of employees must be a whole number');
      return;
    }
    reportPeriodService.updateDenominators(reportPeriodDetails.id, { [field]: parsed })
      .then(() => {
        setReportPeriodDetails({ ...reportPeriodDetails, [field]: parsed });
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
      })
      .catch(err => {
        console.log(err);
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
      });
  }

  // A reopened period is brought into line with the current well-to-tank and losses settings,
  // which can change its figures if it was locked under different ones. Say so when it did.
  const reopenedMessage = (applied?: { includeWtt: boolean, includeTransmissionDistributionLosses: boolean, wttRowsChanged: number, lossRowsChanged: number } | null): string => {
    const changes: string[] = []
    if (applied?.wttRowsChanged) {
      changes.push(applied.includeWtt ? 'well-to-tank emissions are now included' : 'well-to-tank emissions are now excluded')
    }
    if (applied?.lossRowsChanged) {
      changes.push(applied.includeTransmissionDistributionLosses
        ? 'transmission and distribution losses are now included'
        : 'transmission and distribution losses are now excluded')
    }
    if (!changes.length) return 'Updated successfully'
    return `Reopened. To match your current settings, ${changes.join(' and ')} in this period.`
  }

  const reopenHandler = (baseYear: boolean, hasReductionPlan: boolean, ghgWebpagePublic: boolean) => {
    let message = `Are you sure you would like to reopen this report period? This action cannot be undone.`;
    if (baseYear && hasReductionPlan && ghgWebpagePublic) {
      message = `Reopening this report period will delete your reduction targets since they were calculated from this year’s figures, and the ${companyName} Report webpage for this period will no longer be publicly available. Are you sure you would like to proceed? This action cannot be undone.`;
    } else if (baseYear && hasReductionPlan) {
      message = `Reopening this report period will delete your reduction targets, since they were calculated from this year’s figures. Are you sure you would like to reopen this report period? This action cannot be undone.`;
    } else if (ghgWebpagePublic) {
      message = `Reopening this report period will make the ${companyName} Report webpage for this period no longer publicly available. Are you sure you would like to reopen this report period? This action cannot be undone.`;
    }
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: message,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response && id) {
          reportPeriodService.reopenReportPeriod(id)
            .then(response => {
              userNotificationsContext.openSnackbar({ open: true, message: reopenedMessage(response?.upstreamPolicyApplied) });
              getReportPeriodDetails();
              reportPeriodContext.forceUpdateReportPeriods();
            })
            .catch(err => {
              console.log(err)
              userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' });
            })
        }
      })
  }

  const editHandler = (baseYear: boolean, hasReductionPlan: boolean) => {
    if (baseYear && hasReductionPlan) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Confirmation',
        message: `Editing this report period will delete your reduction targets, since they were calculated from this year’s figures. Are you sure you would like to edit this report period?`,
        buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
      })
        .then(response => {
          if (response) {
            openDialogHandler();
          }
        })
    } else {
      openDialogHandler();
    }
  }

  const submitDialogHandler = (values: IPostArchivedReportPeriod) => {
    if (values.id) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        archivedReportPeriodService.editArchivedReportPeriod(values.id, values)
          .then(response => {
            getReportPeriodDetails();
            closeDialogHandler()
            userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
            setActiveStep(0)
            setIsDialogSubmitting(false)
          }
          )
          .catch(err => {
            setErrorMessage(err.response.data.message.message)
            if (err.response.data.message.code === 1) {
              setActiveStep(0)
            }
            setIsDialogSubmitting(false)
          })
      }
    }
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Stack>
      <LoadingPage pageReady={!isLoading && !!reportPeriodDetails}>
        {reportPeriodDetails &&
          <Box component='form' onSubmit={saveDetailsHandler} sx={{ display: 'flex', flexDirection: 'column', pt: 4, pb: isMobileMode ? 3 : 6, pl: isMobileMode ? 3 : 6, pr: isMobileMode ? 3 : 6, mt: isMobileMode ? 5 : 0 }}>
            <Stack display='flex' justifyContent='flex-start'>
              <BackButtonLink buttonText={'Back to report periods'} link={`/settings/company-settings/reporting-periods`} />
              <Stack sx={{ display: 'flex', flexDirection: isMobileMode ? 'column' : 'row', justifyContent: isMobileMode ? 'flex-start' : 'space-between', mt: 1, mb: 2 }}>
                <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <PageTitle title={`${reportPeriodDetails.friendlyName} Report Period Details`} />
                  {reportPeriodDetails.baseYear && <Chip variant='outlined' color={'primary'} label={'Base Year'} sx={{ ml: 2 }} />}
                </Stack>
                <Stack sx={{ display: 'flex', flexDirection: isMobileMode ? 'column' : 'row', justifyContent: isMobileMode ? 'flex-start' : 'flex-end' }}>
                  <Box>
                    {!reportPeriodDetails.baseYear && !hasReductionPlan &&
                      <Button
                        variant='outlined'
                        sx={{ mr: 2 }}
                        onClick={editBaseYearHandler}
                        disabled={areActionsDisabled}
                      >
                        <StarOutline fontSize='small' />
                        <Typography sx={{ pl: 0.5 }}>Make Base Year</Typography>
                      </Button>
                    }
                    <LoadingButton
                      onClick={() => deleteHandler(reportPeriodDetails.baseYear, hasReductionPlan, reportPeriodDetails.manualEntry)}
                      variant='text'
                      sx={{ ml: 2 }}
                      loading={isDeleting}
                      loadingPosition="start"
                      disabled={areActionsDisabled}
                      startIcon={<DeleteOutline fontSize='small' />}
                    >
                      <Typography sx={{ pl: 0.5 }}>Delete</Typography>
                    </LoadingButton>
                    {reportPeriodDetails.manualEntry &&
                      <Button
                        onClick={() => editHandler(reportPeriodDetails.baseYear, hasReductionPlan)}
                        disabled={areActionsDisabled}
                      >
                        <EditOutlined fontSize='small' />
                        <Typography sx={{ pl: 0.5 }}>Edit</Typography>
                      </Button>
                    }
                    {!reportPeriodDetails.manualEntry && reportPeriodDetails.locked &&
                      <Button
                        onClick={() => reopenHandler(reportPeriodDetails.baseYear, hasReductionPlan, reportPeriodDetails.isGaiaReportWebpagePublic)}
                        sx={{ ml: 2 }}
                        disabled={areActionsDisabled}
                      >
                        <LockOpenOutlined fontSize='small' />
                        <Typography sx={{ pl: 0.5 }}>Reopen</Typography>
                      </Button>
                    }
                    {!reportPeriodDetails.manualEntry && !reportPeriodDetails.locked &&
                      <Button
                        onClick={() => setCloseReportDialogOpen(true)}
                        sx={{ ml: 2 }}
                        disabled={areActionsDisabled}
                      >
                        <CloseOutlined fontSize='small' />
                        <Typography sx={{ pl: 0.5 }}>Close</Typography>
                      </Button>
                    }
                    {closeReportDialogOpen &&
                      <CloseReportPeriodDialog
                        open={closeReportDialogOpen}
                        getReportPeriodDetails={getReportPeriodDetails}
                        closeHandler={() => setCloseReportDialogOpen(false)}
                        reportPeriodId={reportPeriodDetails.id} />}
                  </Box>
                  {openDialog && <AddArchivedReportPeriodDialog
                    isSubmitting={isDialogSubmitting}
                    open={openDialog}
                    closeHandler={closeDialogHandler}
                    reportPeriodDetails={reportPeriodDetails}
                    submitHandler={submitDialogHandler}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage} />}
                </Stack>
              </Stack>
            </Stack>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <Tab label="Details" {...a11yProps(0)} />
                <Tab label="Emissions" {...a11yProps(1)} />
                <Tab label="Conversion Factors" {...a11yProps(2)} />
              </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
              <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', mt: 2 }}>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Name </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>{reportPeriodDetails.friendlyName}</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Data Entry </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>{reportPeriodDetails.manualEntry ? 'Manually Entered Data' : 'Calculated Data'}</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Start Date </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>{moment(reportPeriodDetails.startDate).format('DD/MM/YYYY')}</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> End Date </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>{moment(reportPeriodDetails.endDate).format('DD/MM/YYYY')}</Typography>
                </Stack>
                {reportPeriodDetails.lockedDate && <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Close Report Period Date </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>{moment(reportPeriodDetails.lockedDate).format('DD/MM/YYYY')}</Typography>
                </Stack>}
                {reportPeriodDetails.manualEntry || reportPeriodDetails.locked ? (
                  <>
                    <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                      sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                      <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Number of UK Employees </Typography>
                      <Typography variant='body1' sx={{ color: 'text.secondary' }}>{(reportPeriodDetails.numberOfUkEmployees || reportPeriodDetails.numberOfUkEmployees === 0) ? reportPeriodDetails.numberOfUkEmployees : '-'} </Typography>
                    </Stack>
                    <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                      sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                      <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Number of Non UK Employees </Typography>
                      <Typography variant='body1' sx={{ color: 'text.secondary' }}>{(reportPeriodDetails.numberOfNonUkEmployees || reportPeriodDetails.numberOfNonUkEmployees === 0) ? reportPeriodDetails.numberOfNonUkEmployees : '-'} </Typography>
                    </Stack>
                  </>
                ) : (
                  <>
                    <FormTextInputDisplay
                      isEditPage={true}
                      allowEdit={true}
                      editField={editField}
                      setEditField={setEditField}
                      disabled={editField !== 'numberOfUkEmployees'}
                      title='Number of UK Employees'
                      name='numberOfUkEmployees'
                      type='number'
                      value={numberOfUkEmployeesInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumberOfUkEmployeesInput(e.target.value)}
                      touched={!!numberOfUkEmployeesError}
                      errors={numberOfUkEmployeesError}
                      cancelEditHandler={cancelEditHandler}
                      bottomBorder={true}
                      topBorder={false}
                      sideBorders={false}
                    />
                    <FormTextInputDisplay
                      isEditPage={true}
                      allowEdit={true}
                      editField={editField}
                      setEditField={setEditField}
                      disabled={editField !== 'numberOfNonUkEmployees'}
                      title='Number of Non UK Employees'
                      name='numberOfNonUkEmployees'
                      type='number'
                      value={numberOfNonUkEmployeesInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumberOfNonUkEmployeesInput(e.target.value)}
                      touched={!!numberOfNonUkEmployeesError}
                      errors={numberOfNonUkEmployeesError}
                      cancelEditHandler={cancelEditHandler}
                      bottomBorder={true}
                      topBorder={false}
                      sideBorders={false}
                    />
                    {reportPeriodDetails.locationDerivedUkEmployees != null &&
                      reportPeriodDetails.locationDerivedNonUkEmployees != null &&
                      (reportPeriodDetails.numberOfUkEmployees !== reportPeriodDetails.locationDerivedUkEmployees ||
                        reportPeriodDetails.numberOfNonUkEmployees !== reportPeriodDetails.locationDerivedNonUkEmployees) &&
                      <Box sx={{ px: 3, py: 1.5 }}>
                        <Alert severity='info'>
                          These employee figures are set for this period and differ from your current locations
                          (UK: {reportPeriodDetails.locationDerivedUkEmployees}, Non-UK: {reportPeriodDetails.locationDerivedNonUkEmployees}).
                          {' '}
                          <Link component='button' type='button' onClick={applyLocationFigures} sx={{ fontWeight: 600 }}>
                            Use current location figures
                          </Link>
                        </Alert>
                      </Box>
                    }
                  </>
                )}
                {reportPeriodDetails.manualEntry || reportPeriodDetails.locked ? (
                  <>
                    <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                      sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                      <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Total Revenue</Typography>
                      <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                        {(reportPeriodDetails.totalRevenue || reportPeriodDetails.totalRevenue === 0) ? `£ ${reportPeriodDetails.totalRevenue}` : '-'}
                      </Typography>
                    </Stack>
                    {/* Balance sheet is a statutory accounts disclosure input, not part of the
                        frozen snapshot, so it stays editable on locked periods (backfill for
                        reported years). Archived (manually entered) periods stay read-only. */}
                    {reportPeriodDetails.manualEntry ? (
                      <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                        sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                        <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Balance Sheet Total</Typography>
                        <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                          {(reportPeriodDetails.balanceSheetTotal || reportPeriodDetails.balanceSheetTotal === 0) ? `£ ${reportPeriodDetails.balanceSheetTotal}` : '-'}
                        </Typography>
                      </Stack>
                    ) : (
                      <FormTextInputDisplay
                        isEditPage={true}
                        allowEdit={true}
                        editField={editField}
                        setEditField={setEditField}
                        disabled={editField !== 'balanceSheetTotal'}
                        title='Balance Sheet Total'
                        name='balanceSheetTotal'
                        type='number'
                        value={balanceSheetTotalInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBalanceSheetTotalInput(e.target.value)}
                        touched={!!balanceSheetTotalError}
                        errors={balanceSheetTotalError}
                        startAdornment={<Typography variant='body2' sx={{ color: 'text.secondary', mr: 0.5 }}>£</Typography>}
                        cancelEditHandler={cancelEditHandler}
                        bottomBorder={true}
                        topBorder={false}
                        sideBorders={false}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <FormTextInputDisplay
                      isEditPage={true}
                      allowEdit={true}
                      editField={editField}
                      setEditField={setEditField}
                      disabled={editField !== 'totalRevenue'}
                      title='Total Revenue'
                      name='totalRevenue'
                      type='number'
                      value={totalRevenueInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotalRevenueInput(e.target.value)}
                      touched={!!totalRevenueError}
                      errors={totalRevenueError}
                      startAdornment={<Typography variant='body2' sx={{ color: 'text.secondary', mr: 0.5 }}>£</Typography>}
                      cancelEditHandler={cancelEditHandler}
                      bottomBorder={true}
                      topBorder={false}
                      sideBorders={false}
                    />
                    <FormTextInputDisplay
                      isEditPage={true}
                      allowEdit={true}
                      editField={editField}
                      setEditField={setEditField}
                      disabled={editField !== 'balanceSheetTotal'}
                      title='Balance Sheet Total'
                      name='balanceSheetTotal'
                      type='number'
                      value={balanceSheetTotalInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBalanceSheetTotalInput(e.target.value)}
                      touched={!!balanceSheetTotalError}
                      errors={balanceSheetTotalError}
                      startAdornment={<Typography variant='body2' sx={{ color: 'text.secondary', mr: 0.5 }}>£</Typography>}
                      cancelEditHandler={cancelEditHandler}
                      bottomBorder={true}
                      topBorder={false}
                      sideBorders={false}
                    />
                  </>
                )}
                {/* Reporting deadline: user-entered, drives the admin reminder email and banner.
                    Archived (manually entered) periods have no deadline concept, so the rows are
                    hidden; locked periods show read-only (the report is done). */}
                {!reportPeriodDetails.manualEntry && (reportPeriodDetails.locked ? (
                  <>
                    <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                      sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                      <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Reporting Deadline </Typography>
                      <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                        {reportPeriodDetails.reportingDeadline ? moment(reportPeriodDetails.reportingDeadline).format('DD/MM/YYYY') : '-'}
                      </Typography>
                    </Stack>
                    <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                      sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                      <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Deadline Reminder (Days Before) </Typography>
                      <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                        {reportPeriodDetails.reminderDaysBefore != null ? reportPeriodDetails.reminderDaysBefore : '-'}
                      </Typography>
                    </Stack>
                  </>
                ) : (
                  <>
                    <FormDateInputDisplay
                      isEditPage={true}
                      allowEdit={true}
                      editField={editField}
                      setEditField={setEditField}
                      disabled={editField !== 'reportingDeadline'}
                      title='Reporting Deadline'
                      name='reportingDeadline'
                      value={reportingDeadlineInput ? moment(reportingDeadlineInput) : null}
                      onChange={(date: Moment | null) => setReportingDeadlineInput(date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
                      touched={!!reportingDeadlineError}
                      errors={reportingDeadlineError}
                      cancelEditHandler={cancelEditHandler}
                      bottomBorder={true}
                      topBorder={false}
                      sideBorders={false}
                    />
                    <FormTextInputDisplay
                      isEditPage={true}
                      allowEdit={true}
                      editField={editField}
                      setEditField={setEditField}
                      disabled={editField !== 'reminderDaysBefore'}
                      title='Deadline Reminder (Days Before)'
                      name='reminderDaysBefore'
                      type='number'
                      value={reminderDaysBeforeInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReminderDaysBeforeInput(e.target.value)}
                      touched={!!reminderDaysBeforeError}
                      errors={reminderDaysBeforeError}
                      cancelEditHandler={cancelEditHandler}
                      bottomBorder={true}
                      topBorder={false}
                      sideBorders={false}
                    />
                  </>
                ))}
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '29%', minWidth: '100px', mr: 2 }}> Total Offsets </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}> {(reportPeriodDetails.carbonCreditsTCE || reportPeriodDetails.carbonCreditsTCE === 0) ? `${reportPeriodDetails.carbonCreditsTCE} tCO2e` : '-'}</Typography>
                </Stack>
              </Box>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '100%', mb: 2 }}>UK Emissions</Typography>
              <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', mt: 2, mb: 2 }}>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 1 </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeOneTCEUk || reportPeriodDetails.totalScopeOneTCEUk === 0) ? reportPeriodDetails.totalScopeOneTCEUk : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 2 (Market-based)</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeTwoMarketTCEUk || reportPeriodDetails.totalScopeTwoMarketTCEUk === 0) ? reportPeriodDetails.totalScopeTwoMarketTCEUk : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 2 (Location-based)</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeTwoLocationTCEUk || reportPeriodDetails.totalScopeTwoLocationTCEUk === 0) ? reportPeriodDetails.totalScopeTwoLocationTCEUk : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 3 </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeThreeTCEUk || reportPeriodDetails.totalScopeThreeTCEUk === 0) ? reportPeriodDetails.totalScopeThreeTCEUk : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Market-based </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeOneTCEUk != null ||
                        reportPeriodDetails.totalScopeTwoMarketTCEUk != null ||
                        reportPeriodDetails.totalScopeThreeTCEUk != null)
                        ? (reportPeriodDetails.totalScopeOneTCEUk || 0) +
                        (reportPeriodDetails.totalScopeTwoMarketTCEUk || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEUk || 0)
                        : '-'
                    } displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Location-based </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeOneTCEUk != null ||
                        reportPeriodDetails.totalScopeTwoLocationTCEUk != null ||
                        reportPeriodDetails.totalScopeThreeTCEUk != null)
                        ? (reportPeriodDetails.totalScopeOneTCEUk || 0) +
                        (reportPeriodDetails.totalScopeTwoLocationTCEUk || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEUk || 0)
                        : '-'
                    } displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Energy Consumption </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalEnergyConsumptionKwhUk || reportPeriodDetails.totalEnergyConsumptionKwhUk === 0) ? reportPeriodDetails.totalEnergyConsumptionKwhUk : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> kWh</Typography>
                </Stack>
              </Box>
              <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '100%', mb: 2 }}>Overseas Emissions</Typography>
              <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', mt: 2, mb: 2 }}>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}>Scope 1</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeOneTCEGlobal || reportPeriodDetails.totalScopeOneTCEGlobal === 0) ? reportPeriodDetails.totalScopeOneTCEGlobal : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 2 (Market-based)</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeTwoMarketTCEGlobal || reportPeriodDetails.totalScopeTwoMarketTCEGlobal === 0) ? reportPeriodDetails.totalScopeTwoMarketTCEGlobal : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 2 (Location-based)</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeTwoLocationTCEGlobal || reportPeriodDetails.totalScopeTwoLocationTCEGlobal === 0) ? reportPeriodDetails.totalScopeTwoLocationTCEGlobal : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 3</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalScopeThreeTCEGlobal || reportPeriodDetails.totalScopeThreeTCEGlobal === 0) ? `${reportPeriodDetails.totalScopeThreeTCEGlobal}` : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Market-based</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeOneTCEGlobal != null ||
                        reportPeriodDetails.totalScopeTwoMarketTCEGlobal != null ||
                        reportPeriodDetails.totalScopeThreeTCEGlobal != null)
                        ? (reportPeriodDetails.totalScopeOneTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeTwoMarketTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEGlobal || 0)
                        : '-'
                    } displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Location-based</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeOneTCEGlobal != null ||
                        reportPeriodDetails.totalScopeTwoLocationTCEGlobal != null ||
                        reportPeriodDetails.totalScopeThreeTCEGlobal != null)
                        ? (reportPeriodDetails.totalScopeOneTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeTwoLocationTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEGlobal || 0)
                        : '-'
                    } displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Energy Consumption </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalEnergyConsumptionKwhGlobal || reportPeriodDetails.totalEnergyConsumptionKwhGlobal === 0) ? reportPeriodDetails.totalEnergyConsumptionKwhGlobal : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> kWh</Typography>
                </Stack>
              </Box>
              <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '100%', mb: 2 }}>Total Emissions</Typography>
              <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', mt: 2 }}>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 1 </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeOneTCEUk != null || reportPeriodDetails.totalScopeOneTCEGlobal != null)
                        ? (reportPeriodDetails.totalScopeOneTCEUk || 0) + (reportPeriodDetails.totalScopeOneTCEGlobal || 0) : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 2 (Market-based)</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeTwoMarketTCEUk != null || reportPeriodDetails.totalScopeTwoMarketTCEGlobal != null)
                        ? (reportPeriodDetails.totalScopeTwoMarketTCEUk || 0) + (reportPeriodDetails.totalScopeTwoMarketTCEGlobal || 0) : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 2 (Location-based)</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeTwoLocationTCEUk != null || reportPeriodDetails.totalScopeTwoLocationTCEGlobal != null)
                        ? (reportPeriodDetails.totalScopeTwoLocationTCEUk || 0) + (reportPeriodDetails.totalScopeTwoLocationTCEGlobal || 0) : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Scope 3 </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeThreeTCEUk != null || reportPeriodDetails.totalScopeThreeTCEGlobal != null)
                        ? (reportPeriodDetails.totalScopeThreeTCEUk || 0) + (reportPeriodDetails.totalScopeThreeTCEGlobal || 0) : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Market-based</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeOneTCEGlobal != null ||
                        reportPeriodDetails.totalScopeTwoMarketTCEGlobal != null ||
                        reportPeriodDetails.totalScopeThreeTCEGlobal != null ||
                        reportPeriodDetails.totalScopeOneTCEUk != null ||
                        reportPeriodDetails.totalScopeTwoMarketTCEUk != null ||
                        reportPeriodDetails.totalScopeThreeTCEUk != null)
                        ? (reportPeriodDetails.totalScopeOneTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeTwoMarketTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeOneTCEUk || 0) +
                        (reportPeriodDetails.totalScopeTwoMarketTCEUk || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEUk || 0)
                        : '-'
                    } displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Location-based</Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={
                      (reportPeriodDetails.totalScopeOneTCEGlobal != null ||
                        reportPeriodDetails.totalScopeTwoLocationTCEGlobal != null ||
                        reportPeriodDetails.totalScopeThreeTCEGlobal != null ||
                        reportPeriodDetails.totalScopeOneTCEUk != null ||
                        reportPeriodDetails.totalScopeTwoLocationTCEUk != null ||
                        reportPeriodDetails.totalScopeThreeTCEUk != null)
                        ? (reportPeriodDetails.totalScopeOneTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeTwoLocationTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEGlobal || 0) +
                        (reportPeriodDetails.totalScopeOneTCEUk || 0) +
                        (reportPeriodDetails.totalScopeTwoLocationTCEUk || 0) +
                        (reportPeriodDetails.totalScopeThreeTCEUk || 0)
                        : '-'
                    } displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Stack>
                <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'
                  sx={{ pt: 1, pb: 1, pl: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '25%', minWidth: '100px', mr: 2 }}> Total Energy Consumption </Typography>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    <NumericFormat value={(reportPeriodDetails.totalEnergyConsumptionKwhUk != null || reportPeriodDetails.totalEnergyConsumptionKwhGlobal != null)
                      ? (reportPeriodDetails.totalEnergyConsumptionKwhUk || 0) + (reportPeriodDetails.totalEnergyConsumptionKwhGlobal || 0) : '-'} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> kWh</Typography>
                </Stack>
              </Box>
              <ArchivedBreakdownTable
                scopeOneUk={reportPeriodDetails.scopeOneGroupsTCEUk}
                scopeOneOverseas={reportPeriodDetails.scopeOneGroupsTCEGlobal}
                scopeThreeUk={reportPeriodDetails.scopeThreeCategoriesTCEUk}
                scopeThreeOverseas={reportPeriodDetails.scopeThreeCategoriesTCEGlobal}
                scopeOneUkTotal={reportPeriodDetails.totalScopeOneTCEUk}
                scopeOneOverseasTotal={reportPeriodDetails.totalScopeOneTCEGlobal}
                scopeThreeUkTotal={reportPeriodDetails.totalScopeThreeTCEUk}
                scopeThreeOverseasTotal={reportPeriodDetails.totalScopeThreeTCEGlobal}
              />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
              {reportPeriodDetails && (
                <ReportPeriodConversionFactorsTable
                  reportPeriodId={reportPeriodDetails.id || ''}
                  conversionFiguresYear={reportPeriodDetails.conversionFiguresYear ? String(reportPeriodDetails.conversionFiguresYear) : ''}
                  locked={!!reportPeriodDetails.locked}
                  onChangeYear={(conversionSourceId, conversionSourceName, availableYears) => {
                    setSelectedConversionSourceId(conversionSourceId);
                    setSelectedConversionSourceName(conversionSourceName);
                    setSelectedAvailableYears(availableYears);
                    setChangeYearDialogOpen(true);
                  }}
                />
              )}
              {/* Fixed exchange rates belong to a calculated period; an archived (manually entered)
                  period holds typed-in totals with nothing to convert. */}
              {reportPeriodDetails && !reportPeriodDetails.manualEntry && (
                <ReportPeriodExchangeRatesSection
                  reportPeriodId={reportPeriodDetails.id || ''}
                  locked={!!reportPeriodDetails.locked}
                />
              )}
            </CustomTabPanel>
          </Box >
        }
      </LoadingPage>
      <ChangeConversionYearDialog
        open={changeYearDialogOpen}
        conversionSourceName={selectedConversionSourceName}
        conversionSourceId={selectedConversionSourceId}
        currentYear={reportPeriodDetails?.conversionFiguresYear || ''}
        availableYears={selectedAvailableYears}
        reportPeriodId={reportPeriodDetails?.id || ''}
        onClose={() => setChangeYearDialogOpen(false)}
        onSuccess={() => {
          setChangeYearDialogOpen(false);
          getReportPeriodDetails();
        }}
      />
    </Stack>
  )
}